"use client";

import { useState } from "react";
import clsx from "clsx";
import TextContent from "@/components/shared/content/TextContent";
import type { ReactNode } from "react";
import PageJsonLd from "@/components/core/seo/PageJsonLd";
import { faqPageSchema } from "@/lib/schema";

export type FaqItem = { category?: string; question: string; answer: string };

type AccordionItem = {
  id?: string;
  title?: string;
  content?: ReactNode;
  answer?: string;
};

type AccordionGroup = { id?: string; title?: string; items?: AccordionItem[] };

type Props = {
  title?: string;
  groups?: AccordionGroup[];
  groupHeadingLevel?: "h2" | "h3";
  /** Plain-text FAQs from repository content, grouped by category. */
  items?: FaqItem[];
  layout?: "groups" | "faq";
  openMode?: "none" | "one" | "all";
  defaultOpenItemKey?: string;
  allowMultiple?: boolean;
  className?: string;
  standalone?: boolean;
  includeStructuredData?: boolean;
};

function groupFaqItems(items: FaqItem[]): AccordionGroup[] {
  const groups: AccordionGroup[] = [];
  for (const [index, item] of items.entries()) {
    let group = groups.find(group => group.title === item.category);
    if (!group) {
      group = { id: `group-${groups.length}`, title: item.category, items: [] };
      groups.push(group);
    }
    group.items!.push({ id: `faq-${index}`, title: item.question, answer: item.answer });
  }
  return groups;
}

function Arrow({ open }: { open: boolean }) {
  return (
    <span
      className={clsx(
        "inline-flex shrink-0 items-center justify-center transition-transform duration-200",
        open ? "rotate-90" : "rotate-0",
      )}
      aria-hidden="true"
    >
      <svg
        width="8"
        height="18"
        viewBox="0 0 7 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0.244078 0.244078C0.569515 -0.0813593 1.09715 -0.0813593 1.42259 0.244078L6.42259 5.24408C6.74803 5.56951 6.74803 6.09715 6.42259 6.42259L1.42259 11.4226C1.09715 11.748 0.569515 11.748 0.244078 11.4226C-0.0813592 11.0972 -0.0813592 10.5695 0.244078 10.2441L4.65482 5.83333L0.244078 1.42259C-0.0813592 1.09715 -0.0813592 0.569515 0.244078 0.244078Z"
          fill="#E44E47"
        />
      </svg>
    </span>
  );
}

const borderColor = "rgba(255,255,255,0.22)";

export default function AccordionSection({
  title,
  groups: suppliedGroups = [],
  groupHeadingLevel: GroupHeading = "h3",
  items,
  layout = "groups",
  openMode = "none",
  defaultOpenItemKey,
  allowMultiple = true,
  className,
  standalone,
  includeStructuredData = false,
}: Props) {
  const groups = (items ? groupFaqItems(items) : suppliedGroups).map((group, groupIndex) => ({
    ...group,
    id: group.id ?? `group-${groupIndex}`,
    items: (group.items ?? []).map((item, itemIndex) => ({
      ...item,
      id: item.id ?? `group-${groupIndex}-item-${itemIndex}`,
    })),
  }));
  const [openKeys, setOpenKeys] = useState<Set<string>>(() => {
    const keys = groups.flatMap(group => (group.items ?? []).map(item => item.id));
    if (openMode === "all" && allowMultiple) return new Set(keys);
    if (openMode === "one" || openMode === "all") {
      const key = defaultOpenItemKey ?? keys[0];
      return new Set(key ? [key] : []);
    }
    return new Set();
  });

  function toggle(key: string) {
    setOpenKeys(previous => {
      const next = allowMultiple ? new Set(previous) : new Set<string>();
      if (previous.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function renderItems(group: (typeof groups)[number]) {
    return (group.items ?? []).map((item, index, groupItems) => {
      const isOpen = openKeys.has(item.id);
      return (
        <div key={item.id} style={{ borderBottom: index === groupItems.length - 1 ? "0px solid transparent" : `1px solid ${borderColor}` }}>
          <button
            type="button"
            className="flex w-full items-center gap-3 py-5 text-left"
            onClick={() => toggle(item.id)}
            aria-expanded={isOpen}
          >
            <Arrow open={isOpen} />
            <span className="text-[18px] font-medium">{item.title}</span>
          </button>
          <div className={clsx("grid transition-[grid-template-rows] duration-300 ease-in-out", isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}>
            <div className="overflow-hidden">
              {item.answer !== undefined ? (
                <p className="m-0 px-5 pb-5 text-[18px] leading-relaxed font-normal text-grey-550">{item.answer}</p>
              ) : (
                <div className="pb-5 px-5 text-[18px] font-normal leading-relaxed text-grey-550 acccordion-sec">
                  <TextContent>{item.content}</TextContent>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    });
  }

  if (items && items.length === 0) return null;

  return (
    <>
      {includeStructuredData && items && items.length > 0 && <PageJsonLd schema={faqPageSchema(items)} />}
      {layout === "faq" ? (
        <section className={clsx("w-full layout-base mb-20", className)}>
          <div className="col-span-full lg:col-span-3">
            <h2 className="mt-0 text-[32px] font-normal tracking-tight">{title ?? "FAQ"}</h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-4">
            {groups.map((group, index) => (
              <div key={group.id} className={clsx(index > 0 && "mt-10")}>
                {group.title && <h3 className="mt-0 mb-3 font-mono text-[12px] font-semibold uppercase tracking-[0.12em] text-orange-400">{group.title}</h3>}
                <div style={{ borderTop: `1px solid ${borderColor}` }}>{renderItems(group)}</div>
              </div>
            ))}
          </div>
        </section>
      ) : (
        <section className={clsx("mx-auto w-full", standalone ? "container px-4 xl:px-[54px] section-pt-lg section-pb-lg" : "max-w-7xl px-4 sm:px-6 lg:px-8", className)}>
          {title && (
            <div>
              <h2 className="text-[32px] font-normal tracking-tight">{title}</h2>
              <div className="mt-4 h-px w-full" style={{ backgroundColor: borderColor }} />
            </div>
          )}
          <div className={clsx(title && "mt-4")}>
            <div className="space-y-10">
              {groups.map((group, index) => {
                const borderTop = index === 0 ? "0px solid transparent" : `1px solid ${borderColor}`;
                return (
                  <div key={group.id} className="grid grid-cols-1 gap-6 md:grid-cols-12 md:items-start mb-6">
                    <div className="md:col-span-4">
                      <div className="py-5" style={{ borderTop }}>
                        <GroupHeading className="text-[18px] font-medium m-0">{group.title}</GroupHeading>
                      </div>
                    </div>
                    <div className="md:col-span-8" style={{ borderTop }}>{renderItems(group)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
